import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './FreeCandleAccept.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FreeCandleAccept = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  
  const [candle, setCandle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    loadCandle();
  }, [token]);

  const loadCandle = async () => {
    try {
      const response = await axios.get(`${API}/free-candle/${token}`);
      setCandle(response.data);
      setAccepted(response.data.is_accepted);
    } catch (err) {
      setError('Kerze nicht gefunden');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!isAuthenticated) {
      navigate('/login', { 
        state: { returnTo: `/free-candle/${token}`, message: 'Melde dich an, um die Kerze anzunehmen' }
      });
      return;
    }

    setAccepting(true);
    try {
      await axios.post(`${API}/free-candle/${token}/accept`, {}, { withCredentials: true });
      setAccepted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Annehmen');
    } finally {
      setAccepting(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="free-candle-page">
        <div className="free-candle-loading">
          <div className="spinner"></div>
          <p>Lade Kerze...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="free-candle-page">
        <div className="free-candle-error">
          <h2>Oops!</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')}>Zur Startseite</button>
        </div>
      </div>
    );
  }

  return (
    <div className="free-candle-page">
      <div className="free-candle-ambient"></div>
      
      <div className="free-candle-content">
        {/* Tealight visualization */}
        <div className="tealight-display">
          <div className="tealight-cup">
            <div className="tealight-wax"></div>
            <div className="tealight-wick"></div>
            <div className="tealight-flame">
              <div className="flame-inner"></div>
            </div>
            <div className="tealight-glow"></div>
          </div>
        </div>

        <h1 className="free-candle-title">Ein Licht für dich</h1>
        
        {candle.recipient_name && (
          <p className="free-candle-recipient">Liebe/r {candle.recipient_name},</p>
        )}
        
        <p className="free-candle-sender">
          <strong>{candle.sender_name}</strong> hat dir eine Kerze geschenkt!
        </p>

        <div className="free-candle-info">
          <p className="candle-name">🕯️ "{candle.name}"</p>
          {candle.message && (
            <p className="candle-message">"{candle.message}"</p>
          )}
        </div>

        {candle.gift_message && (
          <div className="gift-message-box">
            <p>"{candle.gift_message}"</p>
          </div>
        )}

        <p className="free-candle-expiry">
          Brennt bis: <strong>{formatDate(candle.expires_at)}</strong>
        </p>

        <div className="free-candle-actions">
          {accepted ? (
            <div className="accepted-message">
              <p>✨ Du hast diese Kerze angenommen!</p>
              {candle.accepted_by_name && (
                <p className="accepted-by">Angenommen von: {candle.accepted_by_name}</p>
              )}
              <button onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard">
                Meine Kerzen ansehen
              </button>
            </div>
          ) : (
            <>
              <button 
                className="accept-button"
                onClick={handleAccept}
                disabled={accepting}
                data-testid="accept-free-candle"
              >
                {accepting ? 'Wird angenommen...' : 'Kerze annehmen'}
              </button>
              {!isAuthenticated && (
                <p className="login-hint">Du musst angemeldet sein, um die Kerze anzunehmen</p>
              )}
            </>
          )}
          
          <button 
            className="view-field-button"
            onClick={() => navigate('/')}
          >
            Im Lichtfeld ansehen
          </button>
        </div>
      </div>
    </div>
  );
};

export default FreeCandleAccept;
